import { useMStockTradingSignalQuery } from '@/queries/mStock/mStockQueries'
import { useCallback, useMemo } from 'react'
import { useCoinMeta } from './useCoinMeta.js'
import { hasQueryData } from '@utils/hasQueryData.js'

export const TRADING_SIGNAL_POLLING_PERIOD = '5m-50s'
const EMPTY_EXCHANGES = Object.freeze({})
const EMPTY_SIGNALS = Object.freeze([])

export const useTradingSignal = ({ activeExchange }) => {
  const {
    data: signalData,
    isLoading: isSignalLoading,
    isError: isSignalError,
    refetch: refetchTradingSignal
  } = useMStockTradingSignalQuery()

  const exchanges = signalData?.exchanges ?? EMPTY_EXCHANGES

  const exchangeKey = useMemo(() => {
    if (!activeExchange) {
      return null
    }

    const normalizedActiveExchange = activeExchange.toLowerCase()

    return (
      Object.keys(exchanges).find((key) => key.toLowerCase() === normalizedActiveExchange) ?? null
    )
  }, [activeExchange, exchanges])

  const allAssetIds = useMemo(() => {
    return Object.values(exchanges).flatMap((signals) => {
      if (!Array.isArray(signals)) {
        return []
      }

      return signals.map((signal) => signal.assetId)
    })
  }, [exchanges])

  const {
    coinMetaMap,
    missingAssetIds,
    hasCompletedRetry,
    isLoading: isCoinMetaLoading,
    isError: isCoinMetaError,
    refetch: refetchCoinMeta
  } = useCoinMeta({
    assetIds: allAssetIds,
    enabled: !isSignalLoading
  })

  const data = useMemo(() => {
    if (!exchangeKey) {
      return []
    }

    const signals = exchanges[exchangeKey] ?? EMPTY_SIGNALS

    return signals.map((signal) => {
      const assetId = String(signal.assetId)
      const coinMeta = coinMetaMap[assetId]

      return {
        assetId,
        marketCode: signal.marketCode,
        exchangeCd: signal.exchangeCd,
        exchangeNm: signal.exchangeNm,
        signalTypeName: signal.tradeTypeNm,
        occurredAt: signal.timestamp,
        price: signal.tradePrice,
        changeAmount: signal.change,
        changeRate: signal.changeRate,
        koreanName: coinMeta?.koreanName ?? signal.assetId,
        symbol: coinMeta?.symbol ?? '',
        logo: coinMeta?.logo ?? '',

        metaStatus: coinMeta
          ? 'ready'
          : hasCompletedRetry || isCoinMetaError
            ? 'fallback'
            : 'loading'
      }
    })
  }, [coinMetaMap, exchangeKey, exchanges, hasCompletedRetry, isCoinMetaError])

  const refetchAll = useCallback(() => {
    return Promise.all([refetchTradingSignal(), refetchCoinMeta()])
  }, [refetchCoinMeta, refetchTradingSignal])

  const hasData = hasQueryData(data)

  return {
    data,
    exchanges,
    lastFetchTime: signalData?.lastFetchTime ?? null,
    missingAssetIds,

    isLoading: (isSignalLoading || isCoinMetaLoading) && !hasData,
    isError: isSignalError && !hasData,
    isMetaError: isCoinMetaError,

    // 사용자 재조회용
    refetchAll,
    // 자동 polling 용
    refetchTradingSignal,
    refetchCoinMeta
  }
}
